import React, { Suspense, useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import Tabs from '../../../../../shared/components/ui/Tabs'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useRbac } from '@/shared/auth'
import { isAuthDisconnected } from '@/lib/auth/authDisconnect'
import { OrderStoreProvider } from './table-ops/useOrderStore'
import { useLiveCatalog, LiveCatalogContext } from './table-ops/useLiveCatalog'
import { useTenantOverride } from '@/shared/auth/tenantContext'
import { useSimOrderBridge } from './table-ops/useSimOrderBridge'
import { useTableOpsSoundCues } from './table-ops/sound/useTableOpsSoundCues'
import { lazyWithRetry } from '@/utils/lazyWithRetry'
import { resolveSimTabs, type SimGateMode, type SimTab } from './simTabGates'

// Lazy-load each simulation so only the active one pays the render cost.
const ElectronEquilibriumBox = lazyWithRetry(() => import('./electron-equilibrium/ElectronEquilibriumBox'))
const StringPhysicsBox       = lazyWithRetry(() => import('./string-physics/StringPhysicsBox'))
const MusicSim               = lazyWithRetry(() => import('./music sim/components/SimulationAdmin'))
const TableOpsPanel          = lazyWithRetry(() => import('./table-ops/TableOpsPanel'))
const MercadoPagoPanel       = lazyWithRetry(() => import('./mercadopago/MercadoPagoPanel'))
const TrafficSimPanel        = lazyWithRetry(() => import('./table-ops/TrafficSimPanel'))
const StepRenderPanel        = lazyWithRetry(() => import('./step-render/StepRenderPanel'))
const DaemonOpsPanel         = lazyWithRetry(() => import('./daemon-ops/DaemonOpsPanel'))
const RetailOpsPanel         = lazyWithRetry(() => import('./retail-ops/RetailOpsPanel'))
const MidiDawPanel           = lazyWithRetry(() => import('./midi-daw/components/MidiDawPanel'))
const OledSimPanel           = lazyWithRetry(() => import('./oled-sim/components/OledSimPanel'))
const CadabraCanvas          = lazyWithRetry(() => import('@/features/services/cadabra/components/CadabraCanvas'))

const fallback = (
  <div className="flex items-center justify-center h-40 text-neutral-600 text-sm">
    Loading simulation…
  </div>
)

/** Always-mounted bridge: funnels SSE sim events → OrderStore regardless of active tab */
function SimOrderBridgeMount({ activeTenantId }: { activeTenantId: number | null }) {
  useSimOrderBridge(activeTenantId)
  return null
}

/** Headless audio observer: plays order-enter / station-close cues from store edges. */
function TableOpsSoundMount() {
  useTableOpsSoundCues()
  return null
}

export interface SimulatorPanelProps {
  /**
   * Render mode. `public` restricts tabs to the anonymous-visitor whitelist
   * defined in {@link simTabGates} (Electron, String, KDABRA) and bypasses
   * tenant/auth-dependent wiring. Defaults to `full` for authenticated users.
   */
  mode?: SimGateMode
}

export default function SimulatorPanel({ mode = 'full' }: SimulatorPanelProps) {
  const { isSuperadmin } = useRbac()

  const SIM_TABS = useMemo(
    () => resolveSimTabs({
      isAuthenticated: mode !== 'public',
      isSuperadmin,
      mode,
    }),
    [isSuperadmin, mode],
  )

  const [activeTab, setActiveTab] = useState<SimTab>(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('simTab') as SimTab | null
      if (v && (SIM_TABS as readonly string[]).includes(v)) return v
    } catch {}
    return SIM_TABS[0] ?? 'Music'
  })
  const [sp] = useSearchParams()
  useDocumentTitle('Developer', 'Simulator', activeTab)

  // Sync with URL when BubbleNav navigates while already mounted
  useEffect(() => {
    const v = sp.get('simTab') as SimTab | null
    if (v && (SIM_TABS as readonly string[]).includes(v) && v !== activeTab) setActiveTab(v)
  }, [sp, SIM_TABS])

  // Fall back if active tab got pruned by perm change.
  // Skip during auth disconnect so the URL stays intact for inline re-login.
  useEffect(() => {
    if (isAuthDisconnected()) return
    if (!(SIM_TABS as readonly string[]).includes(activeTab) && SIM_TABS.length)
      setActiveTab(SIM_TABS[0])
  }, [SIM_TABS, activeTab])

  // Tenant context (shared by TableOps + TrafficSim + bridge)
  const { activeTenantId } = useTenantOverride()

  // Tabs that consume the table-ops catalog + sim SSE bridge. Other tabs
  // (KDABRA, Electron, String, MidiDAW, OledSim, MercadoPago, StepRender,
  // RetailOps) MUST NOT trigger the catalog fetch or the SSE connection,
  // otherwise users without table-ops permission see a 403-loop storm
  // (browser EventSource auto-reconnects on every error → 100s req/s).
  const NEEDS_TABLE_OPS_CONTEXT = useMemo(
    () => new Set<SimTab>(['TableOps', 'TrafficSim', 'DaemonOps']),
    [],
  )
  const tableOpsContextActive = NEEDS_TABLE_OPS_CONTEXT.has(activeTab)

  // Pass activeTenantId directly — don't wait for the tenant list to load.
  // activeTenantId is null until a tenant is selected; passing it straight
  // through avoids an initial items fetch without tenant_id → empty results.
  // Pass `undefined` when no consumer tab is active so useLiveCatalog
  // short-circuits its useEffect (it already early-returns on null tenant).
  const catalog = useLiveCatalog(
    tableOpsContextActive ? (activeTenantId || undefined) : undefined,
  )

  return (
    <div className="flex flex-col gap-2 h-full min-h-0">
      <div className="hidden md:block">
        <Tabs
          tabs={[...SIM_TABS]}
          activeTab={activeTab}
          onTabChange={(t) => setActiveTab(t as SimTab)}
          urlKey="simTab"
          urlParams={{ devTab: 'Simulator' }}
        />
      </div>

      <div className="flex-1 min-h-0">
        {/* OrderStore + LiveCatalog providers wrap ALL sim tabs so the KDS
            receives orders even when TrafficSim tab is active.
            SimOrderBridgeMount is gated to table-ops-context tabs only —
            otherwise it opens an EventSource that 403-loops for users
            without table-ops permission. */}
        <LiveCatalogContext.Provider value={catalog}>
          <OrderStoreProvider
            key={activeTenantId ?? 'no-tenant'}
            tenantId={activeTenantId ?? undefined}
          >
            {tableOpsContextActive && (
              <SimOrderBridgeMount activeTenantId={activeTenantId} />
            )}
            {tableOpsContextActive && <TableOpsSoundMount />}
            <Suspense fallback={fallback}>
              {activeTab === 'Electron'     && <ElectronEquilibriumBox />}
              {activeTab === 'String'       && <StringPhysicsBox />}
              {activeTab === 'Music'        && <MusicSim />}
              {activeTab === 'KDABRA'       && <CadabraCanvas />}
              {activeTab === 'MidiDAW'      && <MidiDawPanel />}
              {activeTab === 'OledSim'      && <OledSimPanel />}
              {activeTab === 'TableOps'     && <TableOpsPanel />}
              {activeTab === 'MercadoPago'  && <MercadoPagoPanel />}
              {activeTab === 'StepRender'   && <StepRenderPanel />}
              {activeTab === 'TrafficSim'   && <TrafficSimPanel />}
              {activeTab === 'RetailOps'    && <RetailOpsPanel />}
              {activeTab === 'DaemonOps'    && <DaemonOpsPanel />}
            </Suspense>
          </OrderStoreProvider>
        </LiveCatalogContext.Provider>
      </div>
    </div>
  )
}
